import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useCollectorAccess } from "@/hooks/use-collector-access";
import { useIsAdmin } from "@/hooks/use-user-role";
import LoadingFallback from "@/components/LoadingFallback";

interface CollectorAccessGuardProps {
  children: ReactNode;
  redirectTo?: string;
}

/**
 * Guard component that only allows access if user has collector features enabled.
 * This includes:
 * - Site admins (always have access)
 * - Users with plan_type = 'collector'
 * - Zoo/Sanctuary users who purchased a collector addon
 */
export const CollectorAccessGuard = ({ 
  children, 
  redirectTo = "/admin/billing" 
}: CollectorAccessGuardProps) => {
  const { data: hasAccess, isLoading: accessLoading } = useCollectorAccess();
  const { isAdmin, isLoading: adminLoading } = useIsAdmin();

  if (accessLoading || adminLoading) {
    return <LoadingFallback />;
  }

  // Site admins always have access
  if (isAdmin) {
    return <>{children}</>;
  }

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};
